import { Loader2, Save, Send } from 'lucide-react';
import { Button } from '@/components/atoms/button';

interface ProposalFormActionsProps {
  isEditMode: boolean;
  /** Any submit flow is in flight — disables every button to block double-submits. */
  submitting: boolean;
  /** Only the Save / Save as Draft button is the one spinning. */
  savePending: boolean;
  /** Only the Save & Send button is the one spinning. */
  sendPending: boolean;
  canSave: boolean;
  /** Why saving is blocked, so a greyed-out button isn't a dead end. */
  blockedReason?: string | null;
  /**
   * Whether sending is possible at all. Saving can be fine while sending is
   * not — a customer with neither an email nor a phone has nothing to send to.
   * Defaults to true so a caller that cannot be blocked need not say so.
   */
  canSend?: boolean;
  /** Why sending specifically is blocked, when saving still works. */
  sendBlockedReason?: string | null;
  /** Recovery for transient membership-pricing lookup failures. */
  onRetryBlocked?: () => void;
  onCancel: () => void;
  onSave: () => void;
  onSaveAndSend: () => void;
}

/**
 * Footer actions for the proposal create/edit form.
 *
 * Mirrors EstimateFormActions deliberately: a proposal is created DRAFT and the
 * only way to send it used to be to leave the form, find the detail page and
 * press Send there. "Save as Draft" names the status the form actually
 * produces, and "Save & Send" is offered only when creating — an existing
 * proposal is sent from its own page, where Resend and the status rules live.
 */
export function ProposalFormActions({
  isEditMode,
  submitting,
  savePending,
  sendPending,
  canSave,
  blockedReason,
  canSend = true,
  sendBlockedReason,
  onRetryBlocked,
  onCancel,
  onSave,
  onSaveAndSend,
}: ProposalFormActionsProps) {
  const disabled = submitting || !canSave;
  const sendDisabled = disabled || !canSend;
  // One region, one message. A blocker that stops everything outranks one that
  // stops only sending — showing both would stack two lines in the toolbar and
  // bury the one that matters.
  //
  // The send reason is gated on `canSave` as well, not just on `blockedReason`
  // being absent. A caller can block saving for a reason it has no sentence
  // for, and then an ungated fallback would answer "why is this greyed out?"
  // with the send blocker — a true statement about the wrong thing. Silence is
  // the honest answer there, and it keeps that class of mistake out of reach
  // rather than relying on every save gate remembering to supply a message.
  const shownReason =
    blockedReason ?? (canSave && !canSend ? (sendBlockedReason ?? null) : null);

  return (
    <>
      {/* Always mounted so assistive tech is already observing it when a reason
          appears — a role="status" node inserted at the same moment its text
          arrives is not reliably announced. Truncated for the toolbar; the full
          text stays reachable as the title. */}
      <p
        id="proposal-save-blocked-reason"
        role="status"
        className={
          shownReason
            ? 'max-w-[10rem] truncate text-xs text-destructive sm:max-w-[22rem]'
            : 'sr-only'
        }
        title={shownReason ?? undefined}
      >
        {shownReason}
      </p>
      <Button variant="outline" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      {onRetryBlocked && (
        <Button variant="outline" size="sm" onClick={onRetryBlocked}>
          Retry pricing
        </Button>
      )}
      <Button
        size="sm"
        disabled={disabled}
        aria-describedby={blockedReason ? 'proposal-save-blocked-reason' : undefined}
        onClick={onSave}
      >
        {savePending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : !isEditMode ? (
          <Save className="mr-1.5 h-3.5 w-3.5" />
        ) : null}
        {isEditMode ? 'Save' : 'Save as Draft'}
      </Button>
      {!isEditMode && (
        <Button
          size="sm"
          aria-label="Save and send proposal"
          disabled={sendDisabled}
          // Points at whichever blocker is showing: this button is stopped by
          // both the save blocker and the send-only one.
          aria-describedby={shownReason ? 'proposal-save-blocked-reason' : undefined}
          onClick={onSaveAndSend}
        >
          {sendPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4 sm:mr-2" />
          )}
          <span className="hidden sm:inline">Save &amp; Send</span>
        </Button>
      )}
    </>
  );
}
